/**
 * Profile Saved Sites: resolve preferred site IDs via Coveo sites index.
 * Use savedSitesHeadlessEngine + buildSitesAdvancedQuery (isolated from nearby / LocationSearch).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ResultList,
  ResultListState,
  SearchEngine,
  buildResultList,
  buildResultsPerPage,
  loadAdvancedSearchQueryActions,
  loadQueryActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
} from '@coveo/headless';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { useSelector } from 'react-redux';
import { SITE_FIELDS_TO_INCLUDE } from 'lib/types/coveo/SiteSearchResult';
import { getSavedSiteIds } from 'lib/utils/saveSite';
import { buildSitesAdvancedQuery } from 'lib/utils/coveo/sitesHeadless/sitesAdvancedQuery';
import { RootState } from 'src/store/store';

export interface UseSavedSitesResultListOptions {
  resultsCount?: number;
  latitude?: number;
  longitude?: number;
}

const DEFAULT_RESULTS_COUNT = 150;

function useSavedSitesResultList(
  // eslint-disable-next-line @typescript-eslint/ban-types
  engine: SearchEngine<{}>,
  options: UseSavedSitesResultListOptions = {}
): { state: ResultListState | undefined } {
  const { resultsCount = DEFAULT_RESULTS_COUNT, latitude, longitude } = options;
  const profileData = useSelector((state: RootState) => state.crProfile?.profileData);
  const siteIds = getSavedSiteIds(profileData);
  const siteIdsKey = siteIds.join(',');
  const { sitecoreContext } = useSitecoreContext();
  const [state, setState] = useState<ResultListState>();
  const resultListRef = useRef<ResultList | null>(null);
  const resultsPerPageControllerRef = useRef<ReturnType<typeof buildResultsPerPage> | null>(null);

  const runSearch = useCallback(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    if (typeof window === 'undefined' || siteIds.length === 0) {
      return;
    }

    const { updateQuery } = loadQueryActions(engine);
    const searchQueryActionCreators = loadAdvancedSearchQueryActions(engine);

    const aq = buildSitesAdvancedQuery({
      siteIds,
      latitude,
      longitude,
    });

    engine.dispatch(
      searchQueryActionCreators.updateAdvancedSearchQueries({
        aq,
      })
    );
    engine.dispatch(updateQuery({ q: '' }));

    const { executeSearch } = loadSearchActions(engine);
    const { logInterfaceLoad } = loadSearchAnalyticsActions(engine);
    // savedSitesHeadlessEngine has analytics.enabled false — action is required to run search, not to emit events
    engine.dispatch(executeSearch(logInterfaceLoad()));
  }, [engine, siteIdsKey, latitude, longitude, sitecoreContext?.pageState]);

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }
    if (siteIds.length === 0) {
      return;
    }

    const resultList: ResultList = buildResultList(engine, {
      options: {
        fieldsToInclude: SITE_FIELDS_TO_INCLUDE,
      },
    });

    resultListRef.current = resultList;
    setState(resultList.state);

    const unsubscribe = resultList.subscribe(() => {
      setState(resultList.state);
    });

    return () => {
      unsubscribe();
    };
  }, [engine, siteIdsKey, sitecoreContext?.pageState]);

  useEffect(() => {
    resultsPerPageControllerRef.current = null;
  }, [engine]);

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    if (!resultsPerPageControllerRef.current) {
      resultsPerPageControllerRef.current = buildResultsPerPage(engine, {
        initialState: { numberOfResults: resultsCount },
      });
    } else {
      resultsPerPageControllerRef.current.set(resultsCount);
    }
  }, [engine, resultsCount, sitecoreContext?.pageState]);

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    if (typeof window === 'undefined' || siteIds.length === 0) {
      setState(undefined);
      return;
    }
    runSearch();
  }, [siteIdsKey, runSearch, sitecoreContext?.pageState]);

  return { state };
}

export default useSavedSitesResultList;
